/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTable from 'components/base/AdvanceTable';
import { OutgoingReturning } from 'data/returning';
import returnOutService from 'service/returnOut';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

interface OutgoingReturningTableProps {
  dateFrom?: any;
  dateTo?: any;
  optionSenderId?: any;
  optionStatusId?: any;
  currentPage?: number;
  setCurrentPage?: any;
}

const OutgoingReturningTable = ({
  dateFrom,
  dateTo,
  optionSenderId,
  optionStatusId,
  currentPage,
  setCurrentPage
}: OutgoingReturningTableProps) => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const legalEntityName = useSelector(
    (state: any) => state?.auth?.legal_entity?.name
  );

  const [pageCount, setPageCount] = useState(1);

  const navigateTo = (row: any) => {
    navigate(
      `/returning/outgoing/returning-outgoing-result/${row?.original?.guid}`,
      {
        state: {
          returning_pid: row?.original?.gupid
        }
      }
    );
  };

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  /* prettier-ignore */
  const adjustedDateTo =
    !dateFrom && !dateTo
      ? undefined
      : dateTo === dateFrom
        ? dateFrom + 86400000
        : dateTo
          ? dateTo + 86400000
          : undefined;

  //Get Return
  const { data, refetch, isFetching, isLoading } = useQuery(
    [
      'GET_RETURN_OUT_MAIN',
      dateFrom,
      dateTo,
      optionStatusId,
      optionSenderId,
      currentPage
    ],
    async () => {
      const adjustedDateTo =
        dateTo === dateFrom ? dateFrom + 86400000 : dateTo + 86400000;
      const res = await returnOutService
        .getList({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          status_pid: optionStatusId ? optionStatusId : undefined,
          recipient_pid: optionSenderId ? optionSenderId : undefined,
          date_from: dateFrom,
          date_to: adjustedDateTo
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!dateFrom ||
        !!dateTo ||
        !!adjustedDateTo ||
        !!optionSenderId ||
        !!optionStatusId ||
        !!currentPage
    }
  );

  const loading = isFetching || isLoading;

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const returnData: OutgoingReturning[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    return (
      data?.returns?.map((items: any) => {
        return {
          name: items?.name,
          orders: items?.return_number,
          status: items?.status?.name?.[i18n?.language],
          sender: legalEntityName,
          recipient: items?.legal_entity?.name,
          /* prettier-ignore */
          create_date: formatDateToTimeZone(items?.date_create, timeZone),
          guid: items?.id,
          gupid: items?.pid || 0
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const outgoingTableColumns: ColumnDef<OutgoingReturning>[] = [
    {
      accessorKey: 'orders',
      header: t('application_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'sender',
      header: t('sender'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'recipient',
      header: t('recipient'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          Завершен: { bg: '#BBFBD0', text: '#119C2B' },
          Отправлен: { bg: '#fef3c7', text: '#92400e' },
          Bajarildi: { bg: '#BBFBD0', text: '#119C2B' },
          "Jo'natilgan": { bg: '#fef3c7', text: '#92400e' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'create_date',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: returnData,
    columns: outgoingTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    refetch();
  }, [dateFrom, dateTo, optionSenderId, optionStatusId]);

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          navigateTo={navigateTo}
          isLoading={loading}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </AdvanceTableProvider>
  );
};

export default OutgoingReturningTable;
